import React, { useState, useEffect } from 'react';
import { admin } from '../../services/api';

function BulkDeleteModal({ show, selectedIds = [], onClose, onDeleteComplete, deleteAll = false, totalCount = 0 }) {
  const [layer, setLayer] = useState(1); // 1, 2, or 3
  const [confirmCheckbox, setConfirmCheckbox] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && (deleteAll || selectedIds.length > 0)) {
      setLayer(1);
      setConfirmCheckbox(false);
      setConfirmText('');
      setDeleting(false);
      setResult(null);
      setError(null);
    }
  }, [show, selectedIds, deleteAll]);

  const handleContinue = () => {
    if (layer < 3) {
      setLayer(layer + 1);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    
    try {
      const response = deleteAll
        ? await admin.deleteAllMedications()
        : await admin.bulkDeleteMedications(selectedIds);
      setResult(response.data);
      setTimeout(() => {
        onDeleteComplete();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Delete failed');
      setDeleting(false);
    }
  };

  const deleteCount = deleteAll ? totalCount : selectedIds.length;
  const isDeleteEnabled = confirmCheckbox && confirmText.toUpperCase() === 'DELETE';

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>{deleteAll ? '🗑️ Delete All Medications' : '🗑️ Bulk Delete Medications'}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {result ? (
            // Success view
            <div>
              <div style={{ color: 'green', marginBottom: '16px' }}>
                ✅ Successfully deleted {result.deleted_count} medication(s)
              </div>
              {result.skipped_count > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <strong>Skipped ({result.skipped_count}):</strong>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    {result.skipped_ids.map((item, idx) => (
                      <li key={idx}>
                        ID {item.id}: {item.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Layer 1: Warning with count */}
              {layer >= 1 && (
                <div>
                  <p style={{ marginBottom: '16px' }}>
                    {deleteAll ? (
                      <>You are about to permanently delete <strong>ALL medications (count: {deleteCount})</strong>.</>
                    ) : (
                      <>You are about to permanently delete <strong>{deleteCount}</strong> medication(s).</>
                    )}
                  </p>
                  {!deleteAll && (
                    <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
                      First 10 IDs: {selectedIds.slice(0, 10).join(', ')}
                      {selectedIds.length > 10 ? '...' : ''}
                    </p>
                  )}
                  <div style={{ 
                    background: '#fff3cd', 
                    border: '1px solid #ffc107', 
                    borderRadius: '4px', 
                    padding: '12px',
                    marginBottom: '16px'
                  }}>
                    ⚠️ <strong>This cannot be undone!</strong> All associated reviews and activity logs will also be deleted.
                  </div>
                </div>
              )}

              {/* Layer 2: Checkbox confirmation */}
              {layer >= 2 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={confirmCheckbox}
                      onChange={(e) => setConfirmCheckbox(e.target.checked)}
                      style={{ marginTop: '4px' }}
                    />
                    <span>
                      I understand this cannot be undone and I want to permanently delete {deleteAll ? 'all medications' : 'these medications'}.
                    </span>
                  </label>
                </div>
              )}

              {/* Layer 3: Text confirmation and delete button */}
              {layer >= 3 && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px' }}>
                      Type <strong>DELETE</strong> to confirm:
                    </label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontFamily: 'monospace'
                      }}
                    />
                  </div>

                  {error && (
                    <div style={{ 
                      color: 'red', 
                      marginBottom: '16px',
                      padding: '8px',
                      background: '#ffe6e6',
                      borderRadius: '4px'
                    }}>
                      {error}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn-danger"
                    onClick={handleDelete}
                    disabled={!isDeleteEnabled || deleting}
                    style={{ 
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    {deleting ? 'Deleting...' : deleteAll ? `Delete ALL Medications (${deleteCount})` : `Delete ${deleteCount} Medications`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {layer === 1 && !result && (
            <button type="button" className="btn-secondary" onClick={handleContinue}>
              Continue →
            </button>
          )}
          {layer === 2 && !result && (
            <>
              <button type="button" className="btn-secondary" onClick={() => setLayer(1)}>
                ← Back
              </button>
              <button 
                type="button" 
                className="btn-primary" 
                onClick={handleContinue}
              >
                Continue →
              </button>
            </>
          )}
          {!result && layer === 3 && (
            <button type="button" className="btn-secondary" onClick={() => setLayer(2)}>
              ← Back
            </button>
          )}
          {result && (
            <button type="button" className="btn-primary" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkDeleteModal;
